// Pure time helpers for the Focus timer. No React, no store: easy to test.

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

export type FocusKind = 'focus' | 'break';

/** A finished (or stopped) session. Times are epoch ms. */
export interface FocusSession {
  id: string;
  intent: string;
  kind: FocusKind;
  startedAt: number;
  endedAt: number;
  /** Time on the clock, pauses excluded. */
  focusMs: number;
  plannedMs: number;
  /** Ran to the end instead of being stopped early. */
  completed: boolean;
  /** False until the row reaches the focus_sessions table. */
  synced?: boolean;
}

/** The timer that is running (or paused, or ringing) right now. */
export interface ActiveTimer {
  id: string;
  intent: string;
  kind: FocusKind;
  startedAt: number;
  endsAt: number;
  pausedAt: number | null;
  /** Total time spent paused before the current pause. */
  pausedMs: number;
  /** When the timer reached zero and rang; null while it still counts down. */
  rangAt: number | null;
}

/** Wall-clock "now" for this timer: frozen while paused. */
function effectiveNow(t: ActiveTimer, now: number): number {
  return t.pausedAt ?? now;
}

/** Milliseconds left; negative once it has run over. */
export function remainingMs(t: ActiveTimer, now: number): number {
  return t.endsAt - effectiveNow(t, now);
}

export function plannedMs(t: ActiveTimer): number {
  return t.endsAt - t.startedAt - t.pausedMs;
}

export function elapsedMs(t: ActiveTimer, now: number): number {
  return Math.max(0, effectiveNow(t, now) - t.startedAt - t.pausedMs);
}

/** 0 → 1 over the planned length. */
export function progress(t: ActiveTimer, now: number): number {
  const planned = plannedMs(t);
  if (planned <= 0) return 1;
  return Math.min(1, Math.max(0, elapsedMs(t, now) / planned));
}

/** Share of the 60-tick dial lit for `ms` left of a `plannedMs` session. */
export function dialValue(ms: number, plannedMs: number): number {
  return Math.max(0, ms) / Math.max(plannedMs, HOUR);
}

/** "25:00", "1:05:09", "+02:13" for overtime. */
export function formatClock(ms: number): string {
  const over = ms < 0;
  // Count down in whole seconds the way a kitchen timer does: 24:59.4 shows 25:00.
  const total = over ? Math.floor(-ms / 1000) : Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const body = `${h > 0 ? `${h}:` : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return over ? `+${body}` : body;
}

/** "25 min", "1 h 5 min", "2 h". */
export function formatDuration(ms: number): string {
  const mins = Math.round(ms / MINUTE);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** "1.5h" style total for compact day headers. */
export function formatHours(ms: number): string {
  if (ms <= 0) return '0';
  const h = ms / HOUR;
  if (h < 1) return `${Math.round(ms / MINUTE)}m`;
  return `${h.toFixed(h < 10 ? 1 : 0).replace(/\.0$/, '')}h`;
}

export function formatTimeOfDay(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** "HH:MM" for <input type="time">. */
export function toTimeInputValue(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Next occurrence of "HH:MM" after `now`: 00:30 typed at 23:40 means tonight's
 * 00:30, tomorrow by date. Returns null for unparseable input.
 */
export function timeInputToNextTs(value: string, now: number): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  const d = new Date(now);
  d.setHours(h, m, 0, 0);
  let ts = d.getTime();
  if (ts <= now) {
    d.setDate(d.getDate() + 1);
    ts = d.getTime();
  }
  return ts;
}

export function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Calendar-correct day offset (handles DST, unlike adding 24 h). */
export function addDays(ts: number, days: number): number {
  const d = new Date(ts);
  d.setDate(d.getDate() + days);
  return d.getTime();
}

/** Monday 00:00 of the week containing `ts`. */
export function startOfWeek(ts: number): number {
  const d = new Date(startOfDay(ts));
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d.getTime();
}

/** Focus time (breaks excluded) that falls inside [from, to). */
export function focusMsBetween(sessions: FocusSession[], from: number, to: number): number {
  let total = 0;
  for (const s of sessions) {
    if (s.kind !== 'focus') continue;
    const overlap = Math.min(s.endedAt, to) - Math.max(s.startedAt, from);
    if (overlap <= 0) continue;
    // Scale by the share of the session that was not paused.
    const span = s.endedAt - s.startedAt;
    total += span > 0 ? overlap * (s.focusMs / span) : 0;
  }
  return total;
}

/** Consecutive days, ending today (or yesterday), with any completed focus session. */
export function streakDays(sessions: FocusSession[], now: number): number {
  const days = new Set(
    sessions.filter((s) => s.kind === 'focus' && s.completed).map((s) => startOfDay(s.startedAt)),
  );
  let day = startOfDay(now);
  if (!days.has(day)) day = addDays(day, -1);
  let streak = 0;
  while (days.has(day)) {
    streak += 1;
    day = addDays(day, -1);
  }
  return streak;
}

export interface DayGroup {
  day: number;
  sessions: FocusSession[];
  focusMs: number;
}

/** Newest day first; sessions newest first within a day. */
export function groupByDay(sessions: FocusSession[]): DayGroup[] {
  const groups = new Map<number, FocusSession[]>();
  for (const s of [...sessions].sort((a, b) => b.startedAt - a.startedAt)) {
    const day = startOfDay(s.startedAt);
    const list = groups.get(day);
    if (list) list.push(s);
    else groups.set(day, [s]);
  }
  return [...groups.entries()].map(([day, list]) => ({
    day,
    sessions: list,
    focusMs: list.filter((s) => s.kind === 'focus').reduce((sum, s) => sum + s.focusMs, 0),
  }));
}

export interface TimelineBlock {
  top: number;
  height: number;
}

/** Vertical position of [start, end) on a day column `hourPx` tall per hour. */
export function timelineBlock(start: number, end: number, dayStart: number, hourPx: number, minHeight = 6): TimelineBlock | null {
  const dayEnd = addDays(dayStart, 1);
  const from = Math.max(start, dayStart);
  const to = Math.min(end, dayEnd);
  if (to <= from) return null;
  return {
    top: ((from - dayStart) / HOUR) * hourPx,
    height: Math.max(minHeight, ((to - from) / HOUR) * hourPx),
  };
}

/** Last few distinct intents, most recent first, for one-tap reuse. */
export function recentIntents(sessions: FocusSession[], limit = 4): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of [...sessions].sort((a, b) => b.startedAt - a.startedAt)) {
    const intent = s.intent.trim();
    if (!intent || s.kind !== 'focus') continue;
    const key = intent.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(intent);
    if (out.length >= limit) break;
  }
  return out;
}
